import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/api';
import GitHubConnect from './GitHubConnect';
import GitHubRepoViewer from './GitHubRepoViewer';
import GitHubPushChanges from './GitHubPushChanges';
import '../styles/GitHubPanel.css';

function GitHubPanel({ problemId }) {
  const [activeTab, setActiveTab] = useState('connect');
  const [repoInfo, setRepoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingPR, setCreatingPR] = useState(false);
  const [prResult, setPrResult] = useState(null);
  const [prForm, setPrForm] = useState({ title: '', body: '' });
  const [showPRForm, setShowPRForm] = useState(false);

  const fetchRepoInfo = useCallback(async () => {
    if (!problemId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_BASE_URL}/github/problems/${problemId}/repo`
      );

      if (!response.ok) {
        if (response.status === 401) {
          setRepoInfo(null);
          return;
        }
        throw new Error('Failed to fetch repository info');
      }

      const data = await response.json();
      setRepoInfo(data);

      // If repo is connected, default to browse tab
      if (data.connected) {
        setActiveTab('browse');
      }
    } catch (err) {
      console.error('Error fetching repo info:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [problemId]);

  useEffect(() => {
    fetchRepoInfo();
  }, [fetchRepoInfo]);

  const handleRepoConnect = (result) => {
    setRepoInfo({
      connected: true,
      repository: result.repository,
      branches: [],
      comparison: null
    });
    setActiveTab('browse');
  };

  const handleCreatePR = async () => {
    if (!prForm.title.trim()) {
      alert('Please enter a pull request title');
      return;
    }

    try {
      setCreatingPR(true);
      setError(null);
      setPrResult(null);

      const response = await fetchWithAuth(
        `${API_BASE_URL}/github/problems/${problemId}/pull-request`,
        {
          method: 'POST',
          body: JSON.stringify({
            title: prForm.title,
            body: prForm.body
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create pull request');
      }

      const result = await response.json();
      setPrResult(result);
      setShowPRForm(false);
      setPrForm({ title: '', body: '' });
    } catch (err) {
      console.error('Error creating PR:', err);
      setError(err.message);
    } finally {
      setCreatingPR(false);
    }
  };

  if (loading) {
    return (
      <div className="github-panel loading">
        <div className="loading-spinner"></div>
        <p>Loading GitHub integration...</p>
      </div>
    );
  }

  const isConnected = repoInfo?.connected;

  return (
    <div className="github-panel">
      {/* Header */}
      <div className="panel-header">
        <div className="header-title">
          <span className="github-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
          </span>
          <h2>GitHub Integration</h2>
        </div>
        {isConnected && (
          <div className="repo-badge">
            <span className="repo-name">{repoInfo.repository.owner}/{repoInfo.repository.name}</span>
            <span className="branch-name">{repoInfo.repository.working_branch}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* PR Result */}
      {prResult && (
        <div className="pr-result success">
          <div className="result-header">
            <span className="success-icon">✓</span>
            <span>Pull Request Created!</span>
          </div>
          <div className="result-details">
            <a href={prResult.pull_request.url} target="_blank" rel="noopener noreferrer">
              #{prResult.pull_request.number}: {prResult.pull_request.title}
            </a>
          </div>
          <button className="dismiss-btn" onClick={() => setPrResult(null)}>Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="panel-tabs">
        <button
          className={`tab ${activeTab === 'connect' ? 'active' : ''}`}
          onClick={() => setActiveTab('connect')}
        >
          {isConnected ? 'Settings' : 'Connect'}
        </button>
        {isConnected && (
          <>
            <button
              className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
              onClick={() => setActiveTab('browse')}
            >
              Browse Files
            </button>
            <button
              className={`tab ${activeTab === 'changes' ? 'active' : ''}`}
              onClick={() => setActiveTab('changes')}
            >
              Push Changes
            </button>
            <button
              className={`tab ${activeTab === 'pr' ? 'active' : ''}`}
              onClick={() => setActiveTab('pr')}
            >
              Pull Request
            </button>
          </>
        )}
      </div>

      {/* Tab Content */}
      <div className="panel-content">
        {activeTab === 'connect' && (
          <GitHubConnect
            problemId={problemId}
            onConnect={handleRepoConnect}
            currentRepoUrl={repoInfo?.repository ?
              `https://github.com/${repoInfo.repository.owner}/${repoInfo.repository.name}` :
              null
            }
          />
        )}

        {activeTab === 'browse' && isConnected && (
          <GitHubRepoViewer
            owner={repoInfo.repository.owner}
            repo={repoInfo.repository.name}
            branch={repoInfo.repository.working_branch}
          />
        )}

        {activeTab === 'changes' && isConnected && (
          <GitHubPushChanges
            problemId={problemId}
            onPushComplete={() => fetchRepoInfo()}
          />
        )}

        {activeTab === 'pr' && isConnected && (
          <div className="pr-section">
            <div className="pr-info">
              <h3>Create Pull Request</h3>
              <p>
                Create a pull request to merge changes from{' '}
                <strong>{repoInfo.repository.working_branch}</strong> into{' '}
                <strong>{repoInfo.repository.default_branch}</strong>.
              </p>

              {repoInfo.comparison && (
                <div className="comparison-summary">
                  {repoInfo.comparison.ahead_by > 0 ? (
                    <span className="has-changes">
                      {repoInfo.comparison.ahead_by} commit{repoInfo.comparison.ahead_by !== 1 ? 's' : ''} ready to merge
                    </span>
                  ) : (
                    <span className="no-changes">
                      No commits to merge. Push your changes first.
                    </span>
                  )}
                </div>
              )}
            </div>

            {!showPRForm ? (
              <button
                className="create-pr-btn"
                onClick={() => setShowPRForm(true)}
                disabled={!repoInfo.comparison || repoInfo.comparison.ahead_by === 0}
              >
                Create Pull Request
              </button>
            ) : (
              <div className="pr-form">
                <div className="form-group">
                  <label htmlFor="pr-title">Title</label>
                  <input
                    id="pr-title"
                    type="text"
                    value={prForm.title}
                    onChange={(e) => setPrForm({ ...prForm, title: e.target.value })}
                    placeholder="Pull request title"
                    disabled={creatingPR}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="pr-body">Description</label>
                  <textarea
                    id="pr-body"
                    value={prForm.body}
                    onChange={(e) => setPrForm({ ...prForm, body: e.target.value })}
                    placeholder="Describe your changes..."
                    rows={5}
                    disabled={creatingPR}
                  />
                </div>

                <div className="form-actions">
                  <button
                    className="submit-pr-btn"
                    onClick={handleCreatePR}
                    disabled={creatingPR || !prForm.title.trim()}
                  >
                    {creatingPR ? (
                      <>
                        <span className="loading-spinner small"></span>
                        Creating...
                      </>
                    ) : (
                      'Create Pull Request'
                    )}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowPRForm(false);
                      setPrForm({ title: '', body: '' });
                    }}
                    disabled={creatingPR}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Recent commits */}
            {repoInfo.comparison?.files && repoInfo.comparison.files.length > 0 && (
              <div className="changed-files">
                <h4>Changed Files ({repoInfo.comparison.files.length})</h4>
                <ul>
                  {repoInfo.comparison.files.map((file, index) => (
                    <li key={index} className={`file-change ${file.status}`}>
                      <span className="status-icon">
                        {file.status === 'added' && '+'}
                        {file.status === 'modified' && '~'}
                        {file.status === 'removed' && '-'}
                        {file.status === 'renamed' && '→'}
                      </span>
                      <span className="filename">{file.filename}</span>
                      <span className="changes">
                        +{file.additions} -{file.deletions}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GitHubPanel;
